import { useState, useRef, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "@/components/ui/use-toast";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Info, CheckCircle, Eye, EyeOff, Home, HelpCircle } from "lucide-react";
import CustomSignupCheckout from "@/components/auth/CustomSignupCheckout";
import { monthlyLabel, signupPricingSentence } from "@/lib/pricing";
import { hidePaywallUI } from "@/lib/platform";

const HIDE_PAYWALL = hidePaywallUI();
const AUTH_REDIRECT_ORIGIN = "https://houndpayments.co.uk";

const Signup = () => {
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [showCheckout, setShowCheckout] = useState(false);
  const [signupComplete, setSignupComplete] = useState(false);
  const [promoInput, setPromoInput] = useState("");
  const [promoApplied, setPromoApplied] = useState<{ code: string; description: string; isFullDiscount?: boolean } | null>(null);
  const [promoChecking, setPromoChecking] = useState(false);
  const [promoError, setPromoError] = useState<string | null>(null);
  const signupInProgressRef = useRef(false);

  useEffect(() => {
    // If iOS, redirect to /auth (no paywall surfaces allowed)
    if (HIDE_PAYWALL) navigate("/auth", { replace: true });
  }, [navigate]);

  const handleSignUp = async (e: React.FormEvent) => {
    e.preventDefault();
    if (password !== confirmPassword) {
      toast({ title: "Passwords do not match", description: "Please make sure both passwords are identical.", variant: "destructive" });
      return;
    }
    setLoading(true);
    signupInProgressRef.current = true;
    let keepOpen = false;
    try {
      const { data, error } = await supabase.auth.signUp({
        email,
        password,
        options: { emailRedirectTo: `${AUTH_REDIRECT_ORIGIN}/auth?tab=signin&confirmed=1` },
      });
      if (error) {
        if (error.message.includes("User already registered")) {
          toast({ title: "Account exists", description: "An account with this email already exists. Please sign in instead.", variant: "destructive" });
          navigate("/auth?tab=signin");
        } else {
          toast({ title: "Error creating account", description: error.message, variant: "destructive" });
        }
        return;
      }
      if (data.user) {
        keepOpen = true;
        setShowCheckout(true);
        toast({ title: "Account created!", description: `Enter your card below to start your ${monthlyLabel()}/month subscription.`, variant: "success" });
      }
    } catch (err: any) {
      toast({ title: "Error creating account", description: err.message || "An unexpected error occurred", variant: "destructive" });
    } finally {
      setLoading(false);
      if (!keepOpen) signupInProgressRef.current = false;
    }
  };

  return (
    <div className="min-h-screen flex flex-col bg-background relative">
      <header className="sticky top-0 z-40 w-full bg-background/80 backdrop-blur border-b border-border">
        <div className="container flex items-center justify-between h-16">
          <Link to="/" className="flex items-center gap-2 font-bold text-xl">Hound</Link>
          <div className="flex items-center gap-2">
            <Button size="sm" className="gap-1.5 bg-mint text-mint-foreground hover:bg-mint-dark" onClick={() => navigate("/")}>
              <Home className="h-4 w-4" />Home
            </Button>
            <Button variant="outline" size="sm" className="gap-1.5" onClick={() => navigate("/auth?tab=signin")}>
              Sign In
            </Button>
            <Button variant="ghost" size="sm" className="gap-1.5" onClick={() => navigate("/help")}>
              <HelpCircle className="h-4 w-4" />Help
            </Button>
          </div>
        </div>
      </header>

      <div className="flex-1 flex items-start justify-center px-4 sm:pt-16 pt-[40px] pb-10">
        <div className="w-full max-w-[420px] space-y-6">
          <div className="flex flex-col items-center gap-3 pb-2">
            <span className="text-3xl font-extrabold tracking-tight">Hound</span>
            <p className="text-sm text-muted-foreground text-center">Payments, receipts & reviews — all in one place.</p>
          </div>

          <div className="space-y-1.5">
            <h1 className="text-2xl sm:text-3xl font-bold tracking-tight">Create your Hound account</h1>
            <p className="text-sm text-muted-foreground">Get paid faster and grow your reviews.</p>
          </div>

          {signupComplete ? (
            <Alert className="bg-mint/20 border-mint">
              <CheckCircle className="h-4 w-4 text-mint-dark" />
              <AlertDescription className="text-sm">
                <div className="font-medium mb-2">Account created successfully!</div>
                <p>You can now sign in with your credentials.</p>
                <Button className="w-full mt-4" variant="outline" onClick={() => navigate("/auth?tab=signin")}>Go to Sign In</Button>
              </AlertDescription>
            </Alert>
          ) : (
            <form onSubmit={handleSignUp} className="space-y-4">
              <fieldset disabled={showCheckout} className="space-y-4 disabled:opacity-60">
                <div className="space-y-2">
                  <Label htmlFor="signup-email">Email</Label>
                  <Input id="signup-email" type="email" placeholder="you@example.com" value={email} onChange={(e) => setEmail(e.target.value)} required />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="signup-password">Password</Label>
                  <div className="relative">
                    <Input id="signup-password" type={showPassword ? "text" : "password"} placeholder="••••••••" value={password} onChange={(e) => setPassword(e.target.value)} required minLength={6} className="pr-10" />
                    <Button type="button" variant="ghost" size="sm" className="absolute right-0 top-0 h-full px-3 py-2 hover:bg-transparent" onClick={() => setShowPassword(!showPassword)}>
                      {showPassword ? <EyeOff className="h-4 w-4 text-gray-500" /> : <Eye className="h-4 w-4 text-gray-500" />}
                    </Button>
                  </div>
                  <p className="text-xs text-muted-foreground">Password must be at least 6 characters long</p>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="signup-confirm-password">Confirm Password</Label>
                  <div className="relative">
                    <Input id="signup-confirm-password" type={showConfirmPassword ? "text" : "password"} placeholder="••••••••" value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)} required minLength={6} className="pr-10" />
                    <Button type="button" variant="ghost" size="sm" className="absolute right-0 top-0 h-full px-3 py-2 hover:bg-transparent" onClick={() => setShowConfirmPassword(!showConfirmPassword)}>
                      {showConfirmPassword ? <EyeOff className="h-4 w-4 text-gray-500" /> : <Eye className="h-4 w-4 text-gray-500" />}
                    </Button>
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="signup-promo">Discount code (optional)</Label>
                  {promoApplied ? (
                    <div className="flex items-center justify-between rounded-md border border-mint bg-mint/10 px-3 py-2 text-sm">
                      <span>
                        <span className="font-semibold">{promoApplied.code}</span>
                        <span className="text-muted-foreground"> — {promoApplied.description}</span>
                      </span>
                      <Button type="button" variant="ghost" size="sm" onClick={() => { setPromoApplied(null); setPromoInput(""); setPromoError(null); }}>Remove</Button>
                    </div>
                  ) : (
                    <div className="flex gap-2">
                      <Input id="signup-promo" placeholder="Enter code" value={promoInput} onChange={(e) => { setPromoInput(e.target.value); setPromoError(null); }} disabled={promoChecking} />
                      <Button type="button" variant="outline" disabled={!promoInput.trim() || promoChecking} onClick={async () => {
                        setPromoChecking(true); setPromoError(null);
                        try {
                          const { data, error } = await supabase.functions.invoke("validate-promo-code", { body: { code: promoInput.trim() } });
                          if (error) throw error;
                          if (data?.valid) setPromoApplied({ code: data.code, description: data.description, isFullDiscount: data.is_full_discount });
                          else setPromoError(data?.error || "Invalid code");
                        } catch (e: any) {
                          setPromoError(e?.message || "Could not check code");
                        } finally { setPromoChecking(false); }
                      }}>{promoChecking ? "Checking..." : "Apply"}</Button>
                    </div>
                  )}
                  {promoError && <p className="text-xs text-destructive">{promoError}</p>}
                </div>
              </fieldset>

              {showCheckout && (
                <div className="space-y-2 pt-2 border-t">
                  <div className="space-y-8">
                    <div>
                      <Label>Payment details</Label>
                      <p className="text-xs text-muted-foreground">
                        {signupPricingSentence({ isFullDiscount: promoApplied?.isFullDiscount, isHalfPromo: !!promoApplied && !promoApplied?.isFullDiscount })}
                        {" "}You cannot sign in until this step is finished.
                      </p>
                    </div>
                    <CustomSignupCheckout
                      email={email}
                      returnPath="/auth"
                      promoCode={promoApplied?.code}
                      isFullDiscount={promoApplied?.isFullDiscount}
                      isHalfPromo={!!promoApplied && !promoApplied?.isFullDiscount}
                      onError={(msg) => toast({ title: "Payment form error", description: msg, variant: "destructive" })}
                    />
                  </div>
                </div>
              )}

              {!showCheckout && (
                <Button type="submit" className="w-full h-11 font-semibold" disabled={loading}>
                  {loading ? "Creating account..." : "Sign Up"}
                </Button>
              )}

              {!showCheckout && (
                <Alert>
                  <Info className="h-4 w-4" />
                  <AlertDescription className="text-xs text-muted-foreground">
                    {signupPricingSentence({ isFullDiscount: promoApplied?.isFullDiscount, isHalfPromo: !!promoApplied && !promoApplied?.isFullDiscount })}
                  </AlertDescription>
                </Alert>
              )}

              <div className="text-center text-sm text-muted-foreground pt-2">
                Already have an account?{" "}
                <Link to="/auth?tab=signin" className="text-mint-dark font-medium hover:underline">Sign in</Link>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
};

export default Signup;
